<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      id="addEditConnectorModal"
      v-model="modal.show"
      :title="isEdit ? `Edit Connector` : `Add New Connector`"
      :ok-disabled="isDisable"
      :ok-title="`Submit`"
      @hidden="onHideModal"
      @show="onShowModal"
    >
      <content-spinner :show="modal.loading || loading" />
      <show-error
        :show="showError"
        :text="modalError"
      />
      <b-form
        v-if="!modal.loading && !loading"
        @submit.prevent="handleSubmit(onSubmit)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          v-for="field in connectorFields"
          :id="`addEditConnector-${field.name}-field`"
          :model="form"
          :field="field"
          :disabled="field.disabled"
          @change="(e) => onInputChange(e, field)"
          @blur="(e) => onInputBlur(e, field)"
        />
      </b-form>
      <div
        slot="modal-footer"
        class="d-flex"
      >
        <b-button
          id="addEditConnectorModalCancelBtn"
          class="mt-3 mr-3"
          block
          @click="$bvModal.hide('addEditConnectorModal')"
        >
          Cancel
        </b-button>
        <b-button
          id="addEditConnectorModalOkBtn"
          class="mt-3"
          variant="primary"
          block
          @click.prevent="handleSubmit(onSubmit);"
        >
          Submit
        </b-button>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../core/components/ContentSpinner';
import InputWidget from '../../../core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';
import NoContent from '../../../core/components/NoContent';
import ShowError from '../../../core/components/ShowError';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');
export default {
  name: 'ConnectorModal',
  components: {
    ShowError,
    NoContent,
    InputWidget,
    ContentSpinner,
  },
  data() {
    return {
      isDynamoNew: false,
      newDynamoItem: null,
      showError: false,
      modalError: '',
      loading: false,
      isDisable: false,
      form: {
        vendor: null,
        baseUrl: '',
        authUrl: '',
        username: '',
        password: '',
        accountId: '',
        IsSandbox: false,
        SandboxName: '',
        SandboxAccessToken: '',
        AccessKey: null,
        token: '',
        endpoints: [],
        startingPoints: {},
        environmentParameterName: '',
        ClientId: '',
        server: null,
        region: null,
        version: null,
        tenant: null,
        ClientSecret: '',
        Scope: '',
        APIKey: '',
        mdmModuleName: '',
        mdmFolderName: '',
        instanceURL: '',
      },
      fields: {
        vendor: {
          type: 'select',
          required: true,
          label: 'Vendor',
          name: 'vendor',
          data: {
            options: [
              {value: null, text: 'Select Vendor Type', disabled: true},
              {value: '0', text: 'BlackMountain'},
              {value: '1', text: 'Harvest'},
              {value: '2', text: 'PitchBook'},
              {value: '3', text: 'SQLServer'},
              {value: '4', text: 'Airtable'},
              {value: '5', text: 'Snowflake'},
              {value: '7', text: 'Dynamo'},
              {value: '8', text: 'DealCloud'},
              {value: '9', text: 'Efront'},
              {value: '10', text: 'Salesforce'},
            ],
          },
        },
        tenant: {
          type: 'text',
          required: true,
          label: 'Tenant',
          name: 'tenant',
        },
        baseUrl: {
          type: 'url',
          label: 'Base URL',
          name: 'baseUrl',
        },
      },
    };
  },
  computed: {
    ...mapState({
      modal: state => state.connectorModal,
      connectors: state => state.connectors,
    }),
    isEdit() {
      return !!this.modal.data;
    },
    isEditNewSalesforce() {
      return !!(this.modal.data &&
          this.isSalesforce(this.form.vendor) &&
          this.modal.data.connectorInstanceId);
    },
    isEditNewDynamo() {
      return !!(this.modal.data &&
          this.isDynamo(this.form.vendor) &&
          this.modal.data.connectorInstanceId);
    },
    connectorFields() {
      const fields = {...this.fields};
      this.loading = true;
      fields.baseUrl.label = 'Base URL';
      if (this.isHarvest(this.form.vendor)) {
        this.$nextTick(() => {
          fields.authUrl = {
            type: 'text',
            label: 'Auth URL',
            name: 'authUrl',
          };
          fields.accountId = {
            type: 'text',
            label: 'Account ID',
            name: 'accountId',
          };
          fields.token = {
            type: 'text',
            label: 'Token',
            name: 'token',
          };
          fields.baseUrl.disabled = false;
          if (!this.isEdit) {
            this.form.baseUrl = '';
          }
        });
      } else if (this.isPitchBook(this.form.vendor)) {
        this.$nextTick(() => {
          fields.token = {
            type: 'text',
            label: 'Token',
            name: 'token',
          };
          fields.baseUrl.disabled = true;
          this.form.baseUrl = 'https://api-v2.pitchbook.com/';
        });
      } else if (this.isSQLServer(this.form.vendor)) {
        this.$nextTick(() => {
          fields.baseUrl.label = 'Environment Parameter Name';
        });
      } else if (this.isDealCloud(this.form.vendor)) {
        this.$nextTick(() => {
          fields.authUrl = {
            type: 'text',
            label: 'Auth URL',
            name: 'authUrl',
          };
          fields.ClientId = {
            type: 'text',
            label: 'Client Id',
            name: 'ClientId',
          };
          fields.APIKey = {
            type: 'text',
            label: 'API Key',
            name: 'APIKey',
          };
          fields.baseUrl.disabled = false;
        });
      } else if (this.isDynamo(this.form.vendor)) {
        if (this.isEditNewDynamo || (!this.isEdit && this.isDynamo(this.form.vendor))) {
          fields.tenant.label = 'Connector Name';
          delete fields.baseUrl;

          fields.mdmModuleName = {
            type: 'text',
            label: 'MDM Module Name',
            name: 'mdmModuleName',
            required: true,
          };
          fields.mdmFolderName = {
            type: 'text',
            label: 'MDM Folder Name',
            name: 'mdmFolderName',
            required: true,
          };
          fields.baseUrl = {
            type: 'text',
            label: 'Base Url',
            name: 'baseUrl',
          };
          fields.token = {
            type: 'text',
            label: 'Token',
            name: 'token',
          };
        }
      } else if (this.isSnowflake(this.form.vendor)) {
        fields.server = {
          type: 'text',
          label: 'Server',
          name: 'server',
        };
        fields.region = {
          type: 'text',
          label: 'Region',
          name: 'region',
        };
        fields.username = {
          type: 'text',
          label: 'Username',
          name: 'username',
        };
        fields.password = {
          type: 'text',
          label: 'Password',
          name: 'password',
        };
        delete fields.baseUrl;
      } else if (this.isEfront(this.form.vendor)) {
        fields.username = {
          type: 'text',
          label: 'Username',
          name: 'username',
        };
        fields.password = {
          type: 'password',
          label: 'Password',
          name: 'password',
        };
      } else if (this.isSalesforce(this.form.vendor)) {
        delete fields.baseUrl;
        if (this.isEditNewSalesforce || (!this.isEdit && this.isSalesforce(this.form.vendor))) {
          fields.mdmModuleName = {
            type: 'text',
            label: 'MDM Module Name',
            name: 'mdmModuleName',
            required: true,
          };
          fields.mdmFolderName = {
            type: 'text',
            label: 'MDM Folder Name',
            name: 'mdmFolderName',
            required: true,
          };
          fields.instanceURL = {
            type: 'text',
            label: 'Instance URL',
            name: 'instanceURL',
            placeholder: 'null',
            required: true,
          };
          fields.authUrl = {
            type: 'text',
            label: 'Auth URL',
            name: 'authUrl',
            placeholder: 'null',
            required: true,
          };
        }
        fields.ClientId = {
          type: 'text',
          label: 'Client Id',
          name: 'ClientId',
          required: !!(this.isEditNewSalesforce || !this.isEdit),
        };
        fields.ClientSecret = {
          type: 'password',
          label: 'Client Secret',
          name: 'ClientSecret',
          required: !!(this.isEditNewSalesforce || !this.isEdit),
        };
        fields.username = {
          type: 'text',
          label: 'Username',
          name: 'username',
          required: !!(this.isEditNewSalesforce || !this.isEdit),
        };
        fields.password = {
          type: 'password',
          label: 'Password',
          name: 'password',
          required: !!(this.isEditNewSalesforce || !this.isEdit),
        };
        fields.IsSandbox = {
          type: 'checkbox',
          label: 'Is Sandbox',
          name: 'IsSandbox',
        };
        if (this.form.IsSandbox) {
          if (this.isEdit && !this.isEditNewSalesforce) {
            fields.AccessKey = {
              type: 'text',
              label: 'AccessKey',
              name: 'AccessKey',
            };
          } else {
            fields.SandboxName = {
              type: 'text',
              label: 'Sandbox Name',
              name: 'SandboxName',
            };
            fields.sanboxAccessToken = {
              type: 'text',
              label: 'Sandbox Access Token',
              name: 'SandboxAccessToken',
              required: !!(this.isEditNewSalesforce || !this.isEdit) && this.form.IsSandbox,
            };
          }
        }
        fields.version = {
          type: 'text',
          label: 'Version',
          name: 'version',
          required: true,
        };
        fields.tenant = {
          type: 'text',
          label:
              this.isEditNewSalesforce && !this.edit ||
              this.isSalesforce(this.form.vendor) && !this.isEdit
                ? 'Connector Instance Name'
                : 'Tenant',
          required: true,
          name: 'tenant',
          disabled: this.isEdit && this.isEditNewSalesforce,
        };
      } else if (this.isBlackMountain(this.form.vendor)) {
        fields.authUrl = {
          type: 'text',
          label: 'Auth URL',
          name: 'authUrl',
        };
        fields.ClientId = {
          type: 'text',
          label: 'Client ID',
          name: 'ClientId',
        };
        fields.ClientSecret = {
          type: 'text',
          label: 'Client Secret',
          name: 'ClientSecret',
        };
        fields.Scope = {
          type: 'text',
          label: 'Scope',
          name: 'Scope',
        };
      } else {
        this.$nextTick(() => {
          fields.authUrl = {
            type: 'text',
            label: 'Auth URL',
            name: 'authUrl',
          };
          fields.username = {
            type: 'text',
            label: 'Username',
            name: 'username',
          };
          fields.password = {
            type: 'password',
            label: 'Password',
            name: 'password',
          };
          fields.baseUrl.disabled = false;
          if (!this.isEdit) {
            this.form.baseUrl = '';
          }
        });
      }
      this.$nextTick(() => {
        this.loading = false;
      });
      return fields;
    },
  },
  methods: {
    ...mapActions([
      'hideConnectorModal',
      'saveConnector',
      'saveSalesforceNewConnector',
      'saveDynamoNewConnector',
      'getSalesforceCompleteConfig',
      'getDynamoConnectorsConfig',
      'updateSalesforceNewConnector',
      'updateDynamoNewConnector',
    ]),
    onHideModal() {
      this.form = {
        vendor: null,
        baseUrl: '',
        authUrl: '',
        username: '',
        password: '',
        accountId: '',
        token: '',
        endpoints: [],
        startingPoints: {},
        ClientId: '',
        version: null,
        tenant: null,
        ClientSecret: '',
        Scope: '',
        APIKey: '',
        SandboxName: '',
        SandboxAccessToken: '',
        mdmModuleName: '',
        mdmFolderName: '',
        IsSandbox: false,
      };
      this.isDisable = false;
      this.showError = false;
      this.modalError = '';
      this.hideConnectorModal();
    },
    async onShowModal() {
      if (this.isEdit) {
        const isSalesforceNew = this.modal.data.vendor === 'Salesforce' && this.modal.data.connectorInstanceId;
        this.isDynamoNew = this.modal.data.vendor === 'Dynamo' && this.modal.data.connectorInstanceId;

        if (isSalesforceNew) {
          this.fields.tenant.label = 'Tenant';
          // TODO: refactor after salesforce API latest version is ready
          this.loading = true;
          const {body} = await this.getSalesforceCompleteConfig({configId: this.modal.data.connectorInstanceId});
          this.loading = false;

          this.form.vendor = this.fields.vendor.data.options.find(v => v.text === 'Salesforce').value;
          this.form.tenant = this.modal.data.connectorInstanceName;
          this.form.mdmModuleName = body.mdmModuleName;
          this.form.mdmFolderName = body.mdmFolderName;

          if (body.environmentConfig) {
            this.form.instanceURL = body.environmentConfig.instanceURL ? body.environmentConfig.instanceURL : null;
            this.form.authUrl = body.environmentConfig.authUrl;
            this.form.ClientId = body.environmentConfig.clientId;
            this.form.ClientSecret = body.environmentConfig.clientSecret;
            this.form.username = body.environmentConfig.username;
            this.form.password = body.environmentConfig.password;
            this.form.IsSandbox = body.environmentConfig.isSandbox;

            if (this.form.IsSandbox) {
              this.form.SandboxName = body.environmentConfig.sandboxName;
              this.form.SandboxAccessToken = body.environmentConfig.sandboxAccessToken;
            }

            this.form.version = body.environmentConfig.salesforceVersion;
          }
        } else if (this.isDynamoNew) {
          // TODO: refactor after salesforce API latest version is ready
          this.fields.tenant.label = 'Connector Name';
          this.fields.vendor.disabled = true;
          this.loading = true;
          //TODO use it from state
          const resp = await this.getDynamoConnectorsConfig(this.modal.data.connectorInstanceId);
          this.loading = false;

          this.newDynamoItem = resp;
          this.form.vendor = this.fields.vendor.data.options.find(v => v.text === 'Dynamo').value;
          this.form.tenant = this.modal.data.connectorInstanceName;
          this.form.mdmModuleName = resp.dynamoConnectorConfig.defaultMDMModule;
          this.form.mdmFolderName = resp.dynamoConnectorConfig.defaultMDMFolder;
          this.form.baseUrl = resp.dynamoConnectorConfig.baseUrl;
          this.form.token = resp.dynamoConnectorConfig.apiKey;
          this.form.environmentParameterName = resp.environmentParameterName;
        } else {
          this.fields.tenant.label = 'Tenant';

          this.form = {...this.modal.data};
          const vendorItem = this.fields.vendor.data.options.find(v => [v.value, v.text].includes(this.form.vendor));
          this.form.vendor = vendorItem ? vendorItem.value : null;

          if (Object.keys(JSON.parse(this.modal.data.credentials)).length > 0) {
            const credentials = JSON.parse(this.modal.data.credentials);
            if (this.isHarvest(this.form.vendor)) {
              this.form.accountId = credentials.AccountId;
              this.form.token = credentials.Token;
            } else if (this.isPitchBook(this.form.vendor) || this.isDynamo(this.form.vendor)) {
              this.form.token = credentials.Token;
            } else if (this.isDealCloud(this.form.vendor)) {
              this.form.ClientId = credentials.ClientId;
              this.form.APIKey = credentials.APIKey;
            } else if (this.isSnowflake(this.form.vendor)) {
              this.form.server = credentials.Server;
              this.form.region = credentials.Region;
              this.form.username = credentials.UserName;
              this.form.password = credentials.Password;
            } else if (this.isBlackMountain(this.form.vendor)) {
              this.form.ClientId = credentials.ClientId;
              this.form.ClientSecret = credentials.ClientSecret;
              this.form.Scope = credentials.Scope;
            } else if (this.isSalesforce(this.form.vendor)) {
              this.form.username = credentials.UserName;
              this.form.password = credentials.Password;
              this.form.ClientId = credentials.ClientId;
              this.form.ClientSecret = credentials.ClientSecret;
              this.form.IsSandbox = credentials.IsSandbox;
              this.form.AccessKey = credentials.AccessKey;
              this.form.version = credentials.Version;
              this.form.tenant = credentials.Tenant;
            } else {
              this.form.username = credentials.UserName;
              this.form.password = credentials.Password;
            }
            this.form.tenant = credentials.Tenant;
          }
          this.form = {...this.form};
        }
      } else {
        if (this.isDynamoNew) {
          this.fields.tenant.label = 'Connector Name';
          this.fields.vendor.disabled = false;
        }
      }
    },
    getCredentials(vendor) {
      let credential;

      if (this.isSQLServer(vendor)) {
        credential = {};
      } else if (this.isHarvest(vendor)) {
        credential = {AccountId: this.form.accountId, Token: this.form.token};
      } else if (this.isPitchBook(vendor) || this.isDynamo(vendor)) {
        credential = {Token: this.form.token};
      } else if (this.isDealCloud(vendor)) {
        credential = {ClientId: this.form.ClientId, APIKey: this.form.APIKey};
      } else if (this.isSnowflake(vendor)) {
        credential = {
          Server: this.form.server,
          Region: this.form.region,
          UserName: this.form.username,
          Password: this.form.password,
        };
      } else if (this.isSalesforce(vendor)) {
        credential = {
          ClientId: this.form.ClientId,
          ClientSecret: this.form.ClientSecret,
          UserName: this.form.username,
          Password: this.form.password,
          IsSandbox: this.form.IsSandbox,
          AccessKey: this.form.AccessKey,
          Version: this.form.version,
          Tenant: this.form.tenant,
        };
      } else if (this.isBlackMountain(vendor)) {
        credential = {ClientId: this.form.ClientId, ClientSecret: this.form.ClientSecret, Scope: this.form.Scope};
      } else {
        credential = {UserName: this.form.username, Password: this.form.password};
      }

      credential = {...credential, Tenant: this.form.tenant};

      return JSON.stringify(credential);
    },
    async onSubmit() {
      this.isDisable = true;
      const isSalesforce = this.form.vendor === this.fields.vendor.data.options.find(v => (
        v.text === 'Salesforce'
      )).value;
      const isDynamo = this.form.vendor === this.fields.vendor.data.options.find(v => (
        v.text === 'Dynamo'
      )).value;

      let salesforceInstanceId = null;
      let salesForcePostData = null;
      let dynamoInstanceId = null;
      let dynamoPostData = null;

      if (this.modal.data && Object.hasOwn(this.modal.data, 'connectorInstanceId')) {
        //TODO what if dynamo
        dynamoInstanceId = this.modal.data.connectorInstanceId;
        salesforceInstanceId = this.modal.data.connectorInstanceId;
      }

      const params = {...this.form};
      params.credentials = this.getCredentials(this.form.vendor);

      delete params.username;
      delete params.password;
      delete params.accountId;
      delete params.token;
      delete params.ClientId;
      delete params.ClientSecret;
      delete params.Scope;
      delete params.APIKey;
      delete params.server;
      delete params.region;
      delete params.ClientSecret;
      delete params.version;
      delete params.tenant;
      delete params.IsSandbox;
      delete params.AccessKey;

      let action = 'update';
      if (!params.configurationId && (isSalesforce && !salesforceInstanceId)) {
        params.configurationId = uuidv4();
        action = 'create';
      }
      if (!params.configurationId && (isDynamo && !dynamoInstanceId)) {
        params.configurationId = uuidv4();
        action = 'create';
      }

      // Request data for creating a new salesforce Connector using new API
      if (isSalesforce) {
        salesForcePostData = {
          connectorInstanceName: this.form.tenant,
          moduleName: this.form.mdmModuleName,
          folderName: this.form.mdmFolderName,
          environmentConfig: {
            clientId: this.form.ClientId,
            clientSecret: this.form.ClientSecret,
            username: this.form.username,
            password: this.form.password,
            isSandbox: this.form.IsSandbox,
            salesforceVersion: this.form.version,
            instanceURL: this.form.instanceURL,
            sandboxName: this.form.SandboxName,
            sandboxAccessToken: this.form.SandboxAccessToken,
            authUrl: this.form.authUrl,
          },
        };
      }
      if (isDynamo) {
        dynamoPostData = {
          connectorName: this.form.tenant,
          moduleName: this.form.mdmModuleName,
          folderName: this.form.mdmFolderName,
          environmentConfig: {
            BaseUrl: this.form.baseUrl,
            APIKey: this.form.token,
          },
        };
      }

      if (this.isDynamoNew) {
        dynamoPostData = {
          ...this.newDynamoItem.dynamoConnectorConfig,
          connectorInstanceName: this.form.tenant,
          baseUrl: this.form.baseUrl, //++
          apiKey: this.form.token,//++
          defaultMDMModule: this.form.mdmModuleName,
          defaultMDMFolder: this.form.mdmFolderName,
        };
      }
      let responseSuccess = null;
      let responseBody = {};

      // Action for creating a new Connector instance for salesforce vendor using new API
      if (isSalesforce && action !== 'update') {
        // TODO: check the response
        const {success, body} = await this.saveSalesforceNewConnector(salesForcePostData);
        responseSuccess = success;
        responseBody = body;
      } else if (isSalesforce && action === 'update' && salesforceInstanceId) {
        // Action for creating a new Connector instance for salesforce vendor using new API
        const {success, body} = await this.updateSalesforceNewConnector({
          body: salesForcePostData,
          configurationId: this.modal.data.connectorInstanceId,
        });
        responseSuccess = success;
        responseBody = body;
      } else if (isDynamo && action !== 'update') {
        // TODO: check the response
        const {success, body} = await this.saveDynamoNewConnector(dynamoPostData);
        responseSuccess = success;
        responseBody = body;
      } else if (isDynamo && action === 'update' && dynamoInstanceId) {
        // Action for creating a new Connector instance for dynamo vendor using new API
        const {success, body} = await this.updateDynamoNewConnector({
          body: dynamoPostData,
          configurationId: this.modal.data.connectorInstanceId,
        });
        responseSuccess = success;
        responseBody = body;
      } else {
        const {success, body} = await this.saveConnector({data: params, action});
        responseSuccess = success;
        responseBody = body;
      }

      if (responseSuccess) {
        this.$toasted.success(`Connector ${action === 'create' ? 'created' : 'updated'} successfully`, {
          duration: TOASTER_DURATION,
        });
        this.onHideModal();
      } else {
        this.isDisable = false;
        this.showError = true;
        this.modalError = responseBody.ErrorMessage;
      }
    },
    onInputChange(e, field) {
      if (!this.isEdit && field.name === 'vendor' && e == 10) {
        this.form.tenant = '';
        return;
      }

      if (field.name === 'vendor') {
        this.form.tenant = this.fields.vendor.data.options.find(vendor => vendor.value === e).text;
        this.validateTenantField(this.form.tenant);
      }
    },
    connectorWithTenantNameExist() {
      const getTenant = tenant => tenant ? tenant.toString().trim() : '';

      return this.connectors.some(connector => {
        const credentials = JSON.parse(connector.credentials);
        return getTenant(credentials.Tenant) === getTenant(this.form.tenant) && connector.configurationId !== this.form.configurationId;
      });
    },
    validateTenantField(e) {
      if (this.connectorWithTenantNameExist()) {
        const fieldValue = (typeof e === 'string' ? e : e.target.value).toString().trim();
        this.modalError = `Connector with tenant "${fieldValue}" already exists`;
        this.isDisable = this.showError = true;
      } else {
        this.modalError = '';
        this.isDisable = this.showError = false;
      }
    },
    onInputBlur(e, field) {
      if (field.name === 'tenant') {
        this.validateTenantField(e);
      }
    },
    isHarvest(value) {
      return ['Harvest', '1'].includes(value);
    },
    isPitchBook(value) {
      return ['PitchBook', '2'].includes(value);
    },
    isAirtable(value) {
      return ['Airtable', '4'].includes(value);
    },
    isSnowflake(vendor) {
      return ['Snowflake', '5'].includes(vendor);
    },
    isSQLServer(value) {
      return ['SQLServer', '3'].includes(value);
    },
    isDealCloud(vendor) {
      return ['DealCloud', '8'].includes(vendor);
    },
    isEfront(vendor) {
      return ['Efront', '9'].includes(vendor);
    },
    isBlackMountain(vendor) {
      return ['BlackMountain', '0'].includes(vendor);
    },
    isDynamo(vendor) {
      return ['Dynamo', '7'].includes(vendor);
    },
    isSalesforce(vendor) {
      return ['Salesforce', '10'].includes(vendor);
    },
  },
};
</script>

<style scoped>

</style>
